import { useClientTerminology } from '@/views/list/NetworkTerms';

type UnitDisplayOption = 'long' | 'short';
type Unit = 'mile' | 'foot' | 'meter' | 'kilometer';
export const FOOT_TO_METER = 0.3048;

const MILES_TO_KM_FACTOR = 1.609344;

/**
 * Formats a distance value for display with proper unit conversion and localization.
 * Converts distances to more readable units (e.g., miles < 1 becomes feet, km < 1 becomes meters)
 * and returns a localized formatted string with optional unit labels.
 *
 * @param distance - The distance value to format (undefined returns empty string)
 * @param metric - The unit type of the input distance ('mile' | 'foot' | 'meter' | 'kilometer')
 * @param minDigits - Minimum number of decimal places (default: 2)
 * @param maxDigits - Maximum number of decimal places (default: 2)
 * @param toConvert - Whether to auto-convert to more readable units (default: true)
 * @param showUnits - Whether to include unit labels in the output (default: true)
 * @param unitDisplay - Unit label format: 'long' (e.g., "miles") or 'short' (e.g., "mi") (default: 'long')
 * @returns Localized formatted distance string, or empty string if distance is undefined
 */
export const useFormatDistance = (
  distance?: number,
  metric: Unit = 'mile',
  minDigits: number = 2,
  maxDigits: number = 2,
  toConvert: boolean = true,
  showUnits: boolean = true,
  unitDisplay: UnitDisplayOption = 'long'
) => {
  const intl = useClientTerminology();

  if (distance === undefined) {
    return '';
  }

  if (toConvert) {
    [distance, metric] = convertDistance(distance, metric);
  }

  let unitConfig = {};
  if (showUnits) {
    unitConfig = {
      unit: metric,
      unitDisplay: unitDisplay,
      style: 'unit',
    };
  }

  return intl.formatNumber(distance, {
    minimumFractionDigits: minDigits,
    maximumFractionDigits: maxDigits,
    ...unitConfig,
  });
};

const milesToFeet = (distance: number) => {
  return distance * 5280;
};

export const milesToKms = (distance: number) => {
  return distance * MILES_TO_KM_FACTOR;
};

export const kmsToMiles = (distance: number) => {
  return distance / MILES_TO_KM_FACTOR;
};

export const metersToFeet = (distance: number) => {
  return distance / FOOT_TO_METER;
};

export const squareMetersToSquareFeet = (area: number) => {
  return area / FOOT_TO_METER ** 2;
};

export const getDistanceUnitLong = (
  metric: string,
  plural?: boolean
): 'mile' | 'kilometer' | 'miles' | 'kilometers' => {
  if (metric === 'ft') {
    return plural ? 'miles' : 'mile';
  }
  return plural ? 'kilometers' : 'kilometer';
};

const convertDistance = (distance: number, metric: Unit): [number, Unit] => {
  // Convert miles to feet
  if (distance < 1 && metric === 'mile') {
    metric = 'foot';
    distance = milesToFeet(distance);
  }

  if (metric === 'meter') {
    metric = 'meter';
    distance = distance * FOOT_TO_METER;
  }

  if (metric === 'kilometer') {
    distance = milesToKms(distance);
    if (distance < 1) {
      metric = 'meter';
      distance = distance * 1000;
    }
  }

  return [distance, metric];
};
